import MediaQuery from 'react-responsive';
import React, { useContext } from "react";
import { isBrowser } from "@montana/helpers/browser.helper";
import { FirstRenderContext } from "../../context/first-render.context";
import { RequireAtLeastOne } from '@montana/helpers/typescript-helpers';


interface IResponsiveContent {
    children: React.ReactNode;
}

interface IResponsiveContentSSRProps extends IResponsiveContent {
    includeSSR?: boolean;
}

interface IConditionalRenderProps extends IResponsiveContent {
    ssrContent?: React.ReactNode | null;
}

interface ICustomResponsiveContentSSRBaseProps extends IResponsiveContentSSRProps {
    minWidth: number;
    maxWidth: number;
}

type ICustomResponsiveContentSSRProps = RequireAtLeastOne<ICustomResponsiveContentSSRBaseProps, 'minWidth' | 'maxWidth'>

export enum ScreenSizes {
    Mobile = 480,
    TabletPortrait = 768,
    TabletLandscape = 1024,
    SmallDesktop = 1366,
    Desktop = 1680
}


const ConditionalRender: React.FC<IConditionalRenderProps> = React.memo((props: IConditionalRenderProps): JSX.Element => {

    // The SSR-rendered data might contain content that will not be rendered at the
    // current breakpoint. Sadly, this means that on the initial app render, we will
    // have to render everything twice: once to rehydrate to a "temporary" version that's
    // 1:1 with the SSR-version, to avoid any reconciliation trouble, and then *again* to
    // actually get the breakpoints to "work". It's nasty stuff, but sadly necessary,
    // because we can't control the process any further, and we can't convince React that
    // everything's fine when it clearly isn't.
    // The performance issues raised by this might be worth looking into.
    const isFirstRender = useContext(FirstRenderContext);

    return (
        <>
            {
                !isBrowser || isFirstRender
                    ? props.ssrContent
                    : props.children
            }
        </>
    );
});


export const Responsive: React.FC<ICustomResponsiveContentSSRProps> = React.memo(({ includeSSR, minWidth, maxWidth, children }: ICustomResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={includeSSR ? children : null}>
        <MediaQuery minWidth={minWidth} maxWidth={maxWidth}>
            {children}
        </MediaQuery>
    </ConditionalRender>
));

export const MobileOnly: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery maxWidth={ScreenSizes.Mobile - 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));

export const LargerThanMobile: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.Mobile}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));

export const MobileAndTabletPortrait: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery maxWidth={ScreenSizes.TabletPortrait}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));


export const MobileAndTabletLandscape: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery maxWidth={ScreenSizes.TabletLandscape}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));


export const TabletPortraitOnly: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.TabletPortrait} maxWidth={ScreenSizes.TabletLandscape - 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));


export const TabletLandscapeOnly: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.TabletLandscape} maxWidth={ScreenSizes.Desktop - 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));


export const TabletOnly: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.TabletPortrait} maxWidth={ScreenSizes.Desktop - 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));


export const TabletAndLarger: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.TabletPortrait}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));

export const TabletLandscapeAndLarger: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.TabletLandscape}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));

export const SmallerThanTabletLandscape: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery maxWidth={ScreenSizes.TabletLandscape - 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));


export const DesktopAndLarger: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.TabletLandscape + 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));

export const OnlyLargeDesktop: React.FC<IResponsiveContentSSRProps> = React.memo((props: IResponsiveContentSSRProps): JSX.Element => (
    <ConditionalRender ssrContent={props.includeSSR ? props.children : null}>
        <MediaQuery minWidth={ScreenSizes.SmallDesktop + 1}>
            {props.children}
        </MediaQuery>
    </ConditionalRender>
));

export const ServerRenderOnly: React.FC<IResponsiveContent> = React.memo((props: IResponsiveContent): JSX.Element => (
    <>
        {
            !isBrowser
                ? props.children
                : null
        }
    </>
));
